import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, AlertCircle, Download, Volume2, VolumeX } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface LazyVideoCardProps {
  id: string;
  video_url: string | null;
  audio_url?: string | null;
  poster?: string;
  prompt?: string | null;
  hasPrompt: boolean;
  index: number;
  productName?: string;
}

export const LazyVideoCard = ({
  id,
  video_url,
  audio_url,
  poster,
  prompt: initialPrompt,
  hasPrompt: initialHasPrompt,
  index,
  productName
}: LazyVideoCardProps) => {
  const navigate = useNavigate();
  const { user, plan } = useAuth();
  const isFreePlan = (plan || "free").toLowerCase() === "free";
  const [isVisible, setIsVisible] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [soundOn, setSoundOn] = useState(false); // mobil: dokununca ses aç/kapat
  const [isDownloading, setIsDownloading] = useState(false);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [hasPrompt, setHasPrompt] = useState(initialHasPrompt);
  const cardRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const promptGeneratedRef = useRef(false);

  // Dokunmatik cihaz (hover yok) → görünüre gelince oynat, çıkınca durdur.
  const isTouch = typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches;

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
        // Mobil: kart yeterince görünürse oto-oynat, çıkınca durdur (masaüstünde hover kontrol eder)
        if (isTouch && videoRef.current && !videoError) {
          if (entry.intersectionRatio >= 0.6) videoRef.current.play().catch(() => {});
          else { videoRef.current.pause(); videoRef.current.muted = true; setSoundOn(false); } // ekrandan çıkınca sustur
        }
      },
      { rootMargin: "100px", threshold: [0, 0.6, 1] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isTouch, videoError]);

  // Masaüstü: mouse gelince SESLİ oynat (hover kullanıcı etkileşimi → tarayıcı izin verir),
  // çekilince durdur + sustur + başa sar (kapak görünür). Ses açık oynatma engellenirse sessize düşer.
  const previewPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });
  };
  const previewStop = () => { const v = videoRef.current; if (v) { v.pause(); v.muted = true; try { v.currentTime = 0; } catch { /* ignore */ } } };

  // Mobil: videoya (veya hoparlör rozetine) dokununca ses aç/kapat — dokunuş
  // kullanıcı etkileşimi olduğu için tarayıcı sesli oynatmaya izin verir.
  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.muted) { v.muted = false; v.volume = 1; v.play().catch(() => {}); setSoundOn(true); }
    else { v.muted = true; setSoundOn(false); }
  };

  // Generate prompt automatically when visible and no prompt exists
  useEffect(() => {
    const generatePrompt = async () => {
      // Skip if already has prompt, already generating, already tried, or not visible
      if (hasPrompt || isGeneratingPrompt || promptGeneratedRef.current || !isVisible) {
        return;
      }

      // Need either poster or video_url as image source
      const imageUrl = poster || video_url;
      if (!imageUrl) return;

        // Skip local demo videos that start with /videos/
        if (imageUrl.startsWith('/videos/')) return;

        // Skip video files - AI gateway only accepts image formats (PNG, JPEG, WebP, GIF)
        if (/\.(mp4|webm|mov|avi|mkv)(\?|$)/i.test(imageUrl)) return;

      promptGeneratedRef.current = true;
      setIsGeneratingPrompt(true);

      try {
        const { data, error } = await supabase.functions.invoke('generate-video-prompt', {
          body: { 
            videoId: id,
            imageUrl,
            productName: productName || 'Ürün'
          }
        });

        if (error) {
          console.error('Error generating prompt:', error);
          return;
        }

        if (data?.prompt) {
          setPrompt(data.prompt);
          setHasPrompt(true);
        }
      } catch (err) {
        console.error('Failed to generate prompt:', err);
      } finally {
        setIsGeneratingPrompt(false);
      }
    };

    generatePrompt();
  }, [isVisible, hasPrompt, id, poster, video_url, productName, isGeneratingPrompt]);

  const handleVideoError = () => {
    console.error('Video load error for:', video_url);
    setVideoError(true);
  };

  const handleDownload = async () => {
    if (!video_url) {
      toast.error("Video URL'i bulunamadı");
      return;
    }
    // Ücretsiz planda indirme kapalı → Kredi Al
    if (isFreePlan) {
      toast.error("İndirmek için kredi almalısın. Fiyatlandırmaya yönlendiriliyorsun…");
      setTimeout(() => navigate("/pricing"), 1200);
      return;
    }

    setIsDownloading(true);
    try {
      // For local videos, use direct download
      if (video_url.startsWith('/videos/') || video_url.includes('supabase.co')) {
        const link = document.createElement('a');
        link.href = video_url;
        link.download = `${productName || 'video'}.mp4`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Video indiriliyor...");
      } else {
        // For external URLs (tempfile.aiquickdraw.com etc.), try fetch with no-cors
        try {
          const response = await fetch(video_url);
          if (!response.ok) {
            throw new Error('Video erişilemez durumda');
          }
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${productName || 'video'}.mp4`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          toast.success("Video indirildi!");
        } catch (fetchError) {
          // If fetch fails, open in new tab
          window.open(video_url, '_blank');
          toast.info("Video yeni sekmede açıldı. Sağ tık yaparak indirebilirsiniz.");
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Video indirilemedi. Lütfen tekrar deneyin.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      ref={cardRef}
      className="rounded-lg overflow-hidden bg-card border border-border shadow-lg animate-fade-in transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/10"
      style={{ animationDelay: `${Math.min(index * 100, 500)}ms` }}
    >
      <div
        className="aspect-[9/16] relative group bg-muted"
        onMouseEnter={!isTouch ? previewPlay : undefined}
        onMouseLeave={!isTouch ? previewStop : undefined}
        onClick={isTouch ? toggleSound : undefined}
      >
        {!isVisible ? (
          <Skeleton className="w-full h-full" />
        ) : videoError ? (
          <div className="w-full h-full bg-gradient-to-br from-destructive/10 to-destructive/5 flex flex-col items-center justify-center p-4 text-center">
            <AlertCircle className="h-10 w-10 text-destructive/60 mb-3" />
            <p className="text-sm text-muted-foreground mb-3">Video yüklenemedi</p>
            <Button variant="outline" size="sm" onClick={() => setVideoError(false)}>Tekrar Dene</Button>
          </div>
        ) : (
          <>
            {/* Kapak = videonun ilk karesi (poster + currentTime yamasıyla gri kalmaz). */}
            <video
              ref={videoRef}
              src={video_url || ""}
              poster={poster || undefined}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedMetadata={(e) => { try { (e.currentTarget as HTMLVideoElement).currentTime = 0.05; } catch { /* ignore */ } }}
              onError={handleVideoError}
            />
            {/* Mobil: ses durumu rozeti — dokun sesi aç/kapat (tıklama üstteki kapsayıcıda) */}
            {isTouch && (
              <span className="absolute bottom-2 right-2 rounded-full bg-black/55 p-1.5 text-white pointer-events-none">
                {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </span>
            )}
            {/* İndir (masaüstünde hover'da) — ücretsiz planda kapı açar */}
            <Button
              variant="secondary"
              size="icon"
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
              onClick={(e) => { e.stopPropagation(); handleDownload(); }}
              disabled={isDownloading}
              title="İndir"
            >
              {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            </Button>
          </>
        )}
      </div>
      <div className="p-2 sm:p-3 md:p-4 bg-gradient-to-b from-card to-muted/20 space-y-2">
        {/* "Sistemimizle üretildi" rozeti */}
        <div className="flex items-center justify-center gap-1">
          <Sparkles className="h-3 w-3 text-primary shrink-0" />
          <span className="text-xs sm:text-xs font-bold gradient-text leading-none">Sistemimizle üretildi</span>
        </div>

        {/* AI prompt (varsa) */}
        {hasPrompt && prompt && (
          <p className="text-xs sm:text-xs italic text-muted-foreground leading-snug font-serif line-clamp-2 text-center">
            "{prompt}"
          </p>
        )}

        {/* CTA — izleyip gitme, sen de oluştur */}
        <Button
          size="sm"
          onClick={() => navigate(user ? "/results" : "/auth?mode=signup")}
          className="w-full gap-1 h-8 bg-gradient-primary text-white text-xs sm:text-xs font-semibold hover:opacity-90 active:scale-[0.98] transition"
        >
          <Sparkles className="h-3 w-3" />
          Sen de Oluştur
        </Button>
      </div>
    </div>
  );
};
